import { P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';

import {
  ConsignmentApiModel,
  Country,
  Dimensions,
  ExtraBase,
  InputRuleApiModel,
  MarketplaceOrderApiModel,
  PackageApiModel,
  QuoteApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { GetItemWeight } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { getLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { ContentsSelection } from '../ContentsSelection';
import ItemContentsIntro from '../edit/ItemContentsIntro';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { ConsignmentDetails } from './ConsignmentDetails';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { ProtectionExtraIds } from '../../ExtrasHelper';
import { ExtrasSelection } from '../ExtrasSelection';

interface props {
  item: SmartSendItemApiModel;
  tariffSystem: string;
  hideDimensionsIfNotRequired: boolean;
  getPackages: () => Promise<PackageApiModel[]>;
  onConsignmentUpdate: (c: ConsignmentApiModel) => void;
  onDimensionsUpdate: (c: Dimensions, localUnitsEntry?: LocalUnitsEntry) => void;
  onClose: () => void;
  getCountries: () => Promise<Country[]>;
  getValidation: () => Promise<{ ProductValidation: InputRuleApiModel[] }>;
  onContentUpdate?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  currentSelection: ExtraBase[];
  getQuote: () => Promise<QuoteApiModel>;
  onExtrasUpdate: (extras: ExtraBase[]) => void;
  extrasWhiteList?: string[];
  extrasBlackList?: string[];
  canShowExtras?: boolean;
}

export const ItemContentsPanel: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  return (
    <SidePanelSections>
      <SidePanelSection title={t('SHIPMENT_DETAILS_SUBTITLE', { defaultValue: 'Shipment Details:' })}>
        <ConsignmentDetails
          item={props.item}
          onUpdate={props.onConsignmentUpdate}
          getPackages={props.getPackages}
          dimensionsRequired={props.item.State?.validation?.QuoteState?.Quote?.Service?.DimensionsRequired}
          onDimensionsUpdate={props.onDimensionsUpdate}
          dimensions={props.item.PackageDimensions}
          localUnitsEntry={getLocalUnitsEntry(props.item.Meta)}
          hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
          onClose={props.onClose}
        />
      </SidePanelSection>
      {props.canShowExtras && (
        <ExtrasSelection
          hideLoader={true}
          extrasWhiteList={props.extrasWhiteList}
          extrasBlackList={props.extrasBlackList}
          item={props.item}
          showNonFoundMessage={false}
          getQuote={props.getQuote}
          currentSelection={props.currentSelection}
          onUpdate={(o) => {
            const _extras = props.currentSelection.filter((x) => ProtectionExtraIds.includes(x.Id));
            o?.forEach((e) => {
              if (!_extras.find((x) => x.Id === e.Id)) {
                _extras.push(e);
              }
            });
            props.onExtrasUpdate(_extras);
          }}
          wrapper={(x) => <>{x}</>}
        />
      )}
      <SidePanelSection
        title={t('ECOMMERCE_PRODUCT_SUMMARY_SUBTITLE', { defaultValue: 'eCommerce Product Summary:' })}
        subText={
          <>
            <P2GBox mb={1}>
              <P2GText variant="body2">
                {t('ECOMMERCE_PRODUCT_DESCRIPTION', {
                  defaultValue: 'These are the product details imported from the eCommerce platform.'
                })}
              </P2GText>
            </P2GBox>
            <P2GBox mb={2}>
              <ItemContentsIntro item={props.item} />
            </P2GBox>
          </>
        }
      >
        <ContentsSelection
          item={props.item}
          items={props.item.MarketplaceOrderLines}
          countries={props.getCountries}
          getValidation={props.getValidation}
          tariffSystem={props.tariffSystem}
          onUpdate={props.onContentUpdate}
          totalWeight={GetItemWeight(props.item)}
          currencyCode={props.item.CurrencyCode}
        />
      </SidePanelSection>
    </SidePanelSections>
  );
};
